/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { AssignmentRoles } from 'bundles/assignment-common/types/AssignmentRoles';
import type { FormElementNodeWithId } from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import Question from 'bundles/compound-assessments/components/tunnelvision/Question';
import type {
  AutoGradedQuestionElement,
  ManuallyGradedQuestionElement,
  SubmissionQuestionBase,
} from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { InlineFeedbackRubricContext } from 'bundles/grading/components/staff-graded-assignments/form-parts/InlineFeedbackRubricContext';
import {
  getQuestionPartId,
  processAutoGradedFormElement,
  processManuallyGradedFormElement,
} from 'bundles/grading/utils/formPartUtils';
import { getSubmitterUserId, isManuallyGradedQuestion } from 'bundles/grading/utils/submissionUtils';

type Props = {
  question: SubmissionQuestionBase;
};

const QuestionShell = ({ question }: Props) => {
  const { pageData, submissionData, submissionHeaders } = useContext(SGAPageContext);
  const { isExpanded } = useContext(InlineFeedbackRubricContext);
  if (!question.formElementNode) {
    return null;
  }

  if (!submissionData.data?.submitter) {
    return null;
  }
  // Prepare `formPart` structure to match interface of the `Question` component.
  // The shape of incoming `question` value is slightly different and is described by a different set of types hence it requires massaging
  const element = isManuallyGradedQuestion(question)
    ? processManuallyGradedFormElement(question.formElementNode?.element as ManuallyGradedQuestionElement)
    : processAutoGradedFormElement(question.formElementNode?.element as AutoGradedQuestionElement);

  const formPart = {
    id: getQuestionPartId(question),
    root: {
      element,
      // we don't render `rubrics` as part of `Question` component, even if they're presented
      children: [],
    },
  } as FormElementNodeWithId;
  const teamId = submissionData.data?.isTeamAssignment ? submissionData.data?.submitter?.id : undefined;
  const submissionId = submissionHeaders?.data?.[0]?.id.split('~')?.[1];

  return (
    <Question
      key={`${question.formElementNodeId}~~${submissionData.data.submitter.id}`}
      index={question.position - 1}
      isReadOnly
      isDisabled
      isAutoSaving={false}
      isGraderView
      hideRubric
      role={AssignmentRoles.GRADER}
      formPart={formPart}
      courseId={pageData.courseId}
      itemId={pageData.currentItemId}
      userId={getSubmitterUserId(submissionData.data.submitter)}
      submissionId={submissionId}
      renderFileUploadForPreview={!isExpanded}
      teamId={teamId}
    />
  );
};

export default QuestionShell;
